import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { 
  useCreateMedia, 
  useDeleteMedia 
} from "../../../api/medias/service/index";
import { Modal, ModalBody, Button } from "reactstrap";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTranslation } from 'react-i18next';
import { copyTextToClipboard } from "../../../utils/clipboard";
import { useNotification } from "contexts/NotificationContext";
import { toAppMediaAbsoluteUrl, toUploadRelativePath } from "utils/mediaUrl";
import SecureMediaImg from "components/SecureMediaImg";
import { createExternalMediaShareUrl } from "api/secureMedia";

const MAX_INVENTORY_IMAGES = 20;

const ImagesTab = ({ permission, inventoryData, id, isLoading, activeColor = "secondary",module }) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const { showNotification } = useNotification();

    
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  
  // Theme detection with state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });

  // Listen for theme changes
  useEffect(() => {
    const checkTheme = () => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      setIsDarkMode(newTheme);
    };

    // Check on mount
    checkTheme();

    // Listen for storage events
    window.addEventListener('storage', checkTheme);

    // Also check periodically for same-tab changes
    const interval = setInterval(checkTheme, 100);

    return () => {
      window.removeEventListener('storage', checkTheme);
      clearInterval(interval);
    };
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPreviousImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNextImage();
          break;
        case 'Escape':
          e.preventDefault();
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentImageIndex, lightboxImages]);

  // Lightbox functions
  const openLightbox = (images, startIndex = 0) => {
    setLightboxImages(images);
    setCurrentImageIndex(startIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentImageIndex(0);
    setLightboxImages([]);
  };

  const goToNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === lightboxImages.length - 1 ? 0 : prev + 1
    );
  };

  const goToPreviousImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? lightboxImages.length - 1 : prev - 1
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };
  
  // Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  // Mutations
  const createMediaMutation = useCreateMedia();
  const deleteMediaMutation = useDeleteMedia();



  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const existingImageCount = inventoryData?.length || 0;
  const isAtImageLimit = existingImageCount >= MAX_INVENTORY_IMAGES;
  const remainingImageSlots = Math.max(
    0,
    MAX_INVENTORY_IMAGES - existingImageCount - uploadedFiles.length
  );

  const handleDragOver = (e) => {
    if (isAtImageLimit) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isAtImageLimit) return;

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    if (isAtImageLimit) return;
    const files = Array.from(e.target.files);
    handleFiles(files);
    e.target.value = "";
  };

  const handleFiles = (files) => {
    const slotsAvailable = MAX_INVENTORY_IMAGES - existingImageCount - uploadedFiles.length;
    if (slotsAvailable <= 0) {
      showNotification(
        "warning",
        t("imagesTab.maxImagesReached", { max: MAX_INVENTORY_IMAGES })
      );
      return;
    }

    const filesToProcess = files.slice(0, slotsAvailable);
    if (files.length > slotsAvailable) {
      showNotification(
        "warning",
        t("imagesTab.tooManyImagesSelected", { count: slotsAvailable })
      );
    }

    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const maxDimension = 4096; // Maximum width/height in pixels
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    const validateImageDimensions = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const isValid = img.width <= maxDimension && img.height <= maxDimension;
          resolve({ isValid, width: img.width, height: img.height });
        };
        img.onerror = () => {
          resolve({ isValid: false, width: 0, height: 0 });
        };
        img.src = URL.createObjectURL(file);
      });
    };

    const processFiles = async () => {
      const processedFiles = [];
      
      for (const file of filesToProcess) {
        // Check file type
        const typeValid = allowedTypes.includes(file.type);
        
        // Check file size
        const sizeValid = file.size <= maxFileSize;
        
        // Check image dimensions
        const dimensionCheck = await validateImageDimensions(file);
        const dimensionValid = dimensionCheck.isValid;
        
        // Add validation info to file object
        file.validation = {
          typeValid,
          sizeValid,
          dimensionValid,
          width: dimensionCheck.width,
          height: dimensionCheck.height
        };
        
        // Add dimension info to file object
        file.dimensions = {
          width: dimensionCheck.width,
          height: dimensionCheck.height
        };
        
        processedFiles.push(file);
      }
      
      setUploadedFiles(prev => [...prev, ...processedFiles]);
    };

    processFiles();
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = [t('imagesTab.bytes'), t('imagesTab.kb'), t('imagesTab.mb'), t('imagesTab.gb')];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) return;
    // Filter only valid files for upload
    const validFiles = uploadedFiles.filter(file => 
      file.validation?.typeValid && 
      file.validation?.sizeValid && 
      file.validation?.dimensionValid
    );

    if (validFiles.length === 0) {
      console.log('No valid files to upload');
      return;
    }

    if (existingImageCount + validFiles.length > MAX_INVENTORY_IMAGES) {
      showNotification(
        "warning",
        t("imagesTab.maxImagesReached", { max: MAX_INVENTORY_IMAGES })
      );
      return;
    }

    try {
      const mediaData = {
        [module === 'deals' ? 'deal' : 'inventory']: id,
        type: 'image',
        module: module,
      };

      const files = {
        media: validFiles
      };

      await createMediaMutation.mutateAsync({ data: mediaData, files });
      
      // Clear uploaded files after successful upload
      setUploadedFiles([]);
    } catch (error) {
      console.error('Error uploading images:', error);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    setSelectedDeleteId(mediaId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedDeleteId) {
      try {
        await deleteMediaMutation.mutateAsync({ id: selectedDeleteId });
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  if (isLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className='sr-only'>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      {permission?.inventory_images_add && (
        <div className="col-md-4 mb-4">
          <div className="card">
            <div className="card-header font-weight-bold d-flex justify-content-between align-items-center">
              <span>{t('imagesTab.uploadImages')}</span>
              <small className="text-muted">
                {t('imagesTab.imagesCount', {
                  current: existingImageCount,
                  max: MAX_INVENTORY_IMAGES,
                })}
              </small>
            </div>
            <div className="card-body">
            <div
              className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                if (!isAtImageLimit) {
                  fileInputRef.current?.click();
                }
              }}
              style={{
                border: isDragOver 
                  ? (isDarkMode ? '2px dashed #e14eca' : '2px dashed #1d8cf8')
                  : (isDarkMode ? '2px dashed #344675' : '2px dashed #6c757d'),
                padding: '40px 20px',
                textAlign: 'center',
                cursor: isAtImageLimit ? 'not-allowed' : 'pointer',
                opacity: isAtImageLimit ? 0.6 : 1,
                backgroundColor: isDragOver 
                  ? (isDarkMode ? '#1e1e2d' : '#e3f2fd')
                  : (isDarkMode ? '#27293d' : '#f8f9fa'),
                transition: 'all 0.3s ease',
                marginBottom: '20px',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: isDragOver 
                  ? (isDarkMode 
                      ? '0 2px 8px rgba(225, 78, 202, 0.15), 0 0 0 1px rgba(225, 78, 202, 0.05)'
                      : '0 2px 8px rgba(29, 140, 248, 0.15), 0 0 0 1px rgba(29, 140, 248, 0.05)')
                  : (isDarkMode 
                      ? '0 1px 4px rgba(52, 70, 117, 0.1), 0 0 0 1px rgba(52, 70, 117, 0.05)'
                      : '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)')
              }}
            >
              {/* Gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isDragOver 
                  ? (isDarkMode 
                      ? 'linear-gradient(135deg, rgba(225, 78, 202, 0.1) 0%, rgba(225, 78, 202, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(29, 140, 248, 0.1) 0%, rgba(29, 140, 248, 0.05) 100%)')
                  : (isDarkMode 
                      ? 'linear-gradient(135deg, rgba(52, 70, 117, 0.05) 0%, rgba(52, 70, 117, 0.02) 100%)'
                      : 'linear-gradient(135deg, rgba(108, 117, 125, 0.05) 0%, rgba(108, 117, 125, 0.02) 100%)'),
                pointerEvents: 'none',
                boxShadow: isDragOver 
                  ? (isDarkMode 
                      ? '0 0 8px rgba(225, 78, 202, 0.1)'
                      : '0 0 8px rgba(29, 140, 248, 0.1)')
                  : (isDarkMode 
                      ? '0 0 4px rgba(52, 70, 117, 0.05)'
                      : '0 0 4px rgba(108, 117, 125, 0.05)')
              }}></div>
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <i 
                  className="ni ni-cloud-upload-94"
                  style={{
                    fontSize: '48px',
                    color: isDragOver ? '#e14eca' : '#1d8cf8',
                    marginBottom: '15px',
                    display: 'block',
                    filter: isDragOver ? 'drop-shadow(0 0 10px rgba(225, 78, 202, 0.5))' : 'none'
                  }}
                ></i>
                <i className="tim-icons icon-upload" style={{ 
                  fontSize: '36px', 
                  color: isDarkMode ? '#6c757d' : '#adb5bd',
                  marginBottom: '10px',
                  display: 'block'
                }}></i>
                <h6 className="mb-2" style={{ color: '#495057', fontWeight: '600' }}>
                  {isAtImageLimit ? t('imagesTab.maxImagesReached', { max: MAX_INVENTORY_IMAGES }) : t('imagesTab.uploadImages')}
                </h6>
                <p className="text-muted mb-0" style={{ color: '#6c757d' }}>
                  {isAtImageLimit
                    ? t('imagesTab.removeImagesToUploadMore')
                    : t('imagesTab.dragAndDropImages')}
                </p>
                {!isAtImageLimit && remainingImageSlots < MAX_INVENTORY_IMAGES && (
                  <div style={{
                    marginTop: '10px',
                    fontSize: '12px',
                    color: '#6c757d'
                  }}>
                    {t('imagesTab.remainingImageSlots', { count: remainingImageSlots })}
                  </div>
                )}
                <div style={{
                  marginTop: '15px',
                  fontSize: '12px',
                  color: '#adb5bd'
                }}>
                  {t('imagesTab.supportsFormats')}
                </div>
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: '#adb5bd'
                }}>
                  {t('imagesTab.maxImagesPerVehicle', { max: MAX_INVENTORY_IMAGES })}
                </div>
                <div style={{
                  marginTop: '8px',
                  fontSize: '11px',
                  color: '#6c757d'
                }}>
                  {t('imagesTab.recommendedDimensions')}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                disabled={isAtImageLimit}
                style={{ display: 'none' }}
              />
            </div>

            {/* Uploaded Files Preview */}
            {uploadedFiles.length > 0 && (
              <div className="uploaded-files">
                <h6 className="mb-3" style={{ color: '#ffffff', fontWeight: '600' }}>
                  Selected Files ({uploadedFiles.length})
                </h6>
                <div className="row">
                  {uploadedFiles.map((file, index) => {
                    const isValid = file.validation?.typeValid && file.validation?.sizeValid && file.validation?.dimensionValid;
                    const hasError = !isValid;
                    
                    return (
                      <div className="col-6 mb-2" key={index}>
                        <div className="card" style={{
                          backgroundColor: '#27293d',
                          border: hasError ? '2px solid #fd5d93' : '1px solid rgba(255, 255, 255, 0.1)',
                          overflow: 'hidden',
                          boxShadow: isDarkMode 
                            ? '0 1px 4px rgba(52, 70, 117, 0.1), 0 0 0 1px rgba(52, 70, 117, 0.05)'
                            : '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)',
                          transition: 'all 0.3s ease'
                        }}>
                          <img
                            src={URL.createObjectURL(file)}
                            className="card-img-top"
                            alt={`Preview ${index + 1}`}
                            style={{ height: '80px', objectFit: 'cover' }}
                          />
                          <div className="card-body p-2">
                            <small className="text-muted d-block text-truncate" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                              {file.name}
                            </small>
                            <small className="text-muted d-block" style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>
                              {formatFileSize(file.size)} • {file.type.split('/')[1].toUpperCase()}
                            </small>
                            {file.dimensions && (
                              <small className="text-muted d-block" style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>
                                {file.dimensions.width} × {file.dimensions.height}px
                              </small>
                            )}
                            {hasError && (
                              <div style={{ marginTop: '5px' }}>
                                {!file.validation?.typeValid && (
                                  <small className="text-danger d-block" style={{ fontSize: '10px' }}>
                                    <i className='tim-icons icon-alert-circle'></i> {t('imagesTab.invalidFileType')}
                                  </small>
                                )}
                                {!file.validation?.sizeValid && (
                                  <small className="text-danger d-block" style={{ fontSize: '10px' }}>
                                    <i className='tim-icons icon-alert-circle'></i> {t('imagesTab.fileTooLarge')}
                                  </small>
                                )}
                                {!file.validation?.dimensionValid && (
                                  <small className="text-danger d-block" style={{ fontSize: '10px' }}>
                                    <i className='tim-icons icon-alert-circle'></i> {t('imagesTab.invalidDimensions')}
                                  </small>
                                )}
                              </div>
                            )}
                            
                            <button
                              type="button"
                              className="btn btn-sm btn-danger mt-1"
                              onClick={() => removeFile(index)}
                              style={{
                                fontSize: '12px'
                              }}
                            >
                              <i className="tim-icons icon-simple-remove"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Upload Button */}
                {(() => {
                  const validFiles = uploadedFiles.filter(file => 
                    file.validation?.typeValid && 
                    file.validation?.sizeValid && 
                    file.validation?.dimensionValid
                  );
                  const hasValidFiles = validFiles.length > 0;
                  
                  return (
                    <button
                      type="button"
                      className="btn btn-sm btn-block mt-3"
                      onClick={handleUpload}
                      disabled={createMediaMutation.isLoading || !hasValidFiles}
                      style={{
                        ...(hasValidFiles ? activeColorStyle : {
                          background: '#6c757d',
                          backgroundImage: 'linear-gradient(to bottom left, #6c757d, #6c757d)',
                          borderColor: '#6c757d'
                        }),
                        color: 'white',
                        fontWeight: '600',
                        opacity: hasValidFiles ? 1 : 0.6
                      }}
                    >
                      {createMediaMutation.isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Uploading...
                        </>
                      ) : (
                        hasValidFiles ? t('imagesTab.uploadImagesButton') : t('imagesTab.noValidFilesToUpload')
                      )}
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
      )}
      <div className={`${permission?.inventory_images_add ? 'col-md-8' : 'col-md-12'} mb-4`}>
        <div className="card">
          <div className="card-header font-weight-bold">{t('imagesTab.imageGallery')}</div>
          <div className="card-body">
            {/* Real Media Data */}
            {inventoryData && inventoryData?.length > 0 ? (
              <div className="row">
                {inventoryData?.map((media, index) => (
                  <div className="col-md-3 mb-3" key={media.id}>
                    <div className="card" style={{
                      boxShadow: isDarkMode 
                        ? '0 1px 4px rgba(52, 70, 117, 0.1), 0 0 0 1px rgba(52, 70, 117, 0.05)'
                        : '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const images = inventoryData.map((m) => ({
                        url: toUploadRelativePath(m.media?.url || ""),
                        thumbnail: toUploadRelativePath(
                          m.media?.formats?.thumbnail?.url || m.media?.url || ""
                        ),
                        category: m.category,
                        id: m.id,
                      }));
                      openLightbox(images, index);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = isDarkMode 
                        ? '0 4px 12px rgba(52, 70, 117, 0.2), 0 0 0 1px rgba(52, 70, 117, 0.1)'
                        : '0 4px 12px rgba(108, 117, 125, 0.2), 0 0 0 1px rgba(108, 117, 125, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isDarkMode 
                        ? '0 1px 4px rgba(52, 70, 117, 0.1), 0 0 0 1px rgba(52, 70, 117, 0.05)'
                        : '0 1px 4px rgba(108, 117, 125, 0.1), 0 0 0 1px rgba(108, 117, 125, 0.05)';
                    }}
                    >
                      <div style={{ position: 'relative' }}>
                        <SecureMediaImg
                          src={media.media?.formats?.thumbnail?.url || media.media?.url || ""}
                          alt={media.category || "Inventory image"}
                          className="card-img-top"
                          style={{ height: "150px", width: "100%", objectFit: "cover" }}
                          fallback={
                            <div
                              className="card-img-top d-flex align-items-center justify-content-center"
                              style={{ height: 150, background: isDarkMode ? "#1e1e2d" : "#e9ecef" }}
                            >
                              <i className="tim-icons icon-image-02" />
                            </div>
                          }
                        />
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0, 0, 0, 0.7)',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '14px'
                        }}>
                          <i className="tim-icons icon-image-02"></i>
                        </div>
                      </div>
                      <div className="card-body p-2">
                        <small className="text-muted d-block">{media.category}</small>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted" style={{ fontSize: '11px' }}>
                            Click to view
                          </small>
                        <div className="d-flex">
                            {permission?.inventory_images_add && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMedia(media.id);
                              }}
                              disabled={deleteMediaMutation.isLoading}
                              style={{
                                fontSize: '12px'
                              }}
                            >
                              <i className="tim-icons icon-trash-simple"></i>
                            </button>
                            )}
                              <div className="d-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const imageUrl = toAppMediaAbsoluteUrl(media.media?.url || "");
                                if (!imageUrl) {
                                  showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                  return;
                                }
                                const copied = await copyTextToClipboard(imageUrl);
                                if (copied) {
                                  showNotification(
                                    "success",
                                    t("filesTab.internalLinkCopied", "Internal link copied (login required).")
                                  );
                                } else {
                                  showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                }
                              }}
                              style={{
                                fontSize: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                padding: 0,
                                backgroundColor: "#344767",
                                borderColor: "#344767",
                                backgroundImage: "none",
                                color: "#fff",
                              }}
                              title={t("filesTab.copyInternalLink", "Copy internal link (company only)")}
                            >
                              <i className="fas fa-link" style={{ color: "#fff" }}></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const shareUrl = await createExternalMediaShareUrl(media.media?.url || "");
                                  const copied = await copyTextToClipboard(shareUrl);
                                  if (copied) {
                                    showNotification(
                                      "success",
                                      t("filesTab.externalLinkCopied", "External share link copied.")
                                    );
                                  } else {
                                    showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                  }
                                } catch {
                                  showNotification("warning", t("filesTab.linkCopyFailed", "Could not copy link."));
                                }
                              }}
                              style={{
                                fontSize: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                padding: 0,
                                backgroundColor: "#2dce89",
                                borderColor: "#2dce89",
                                backgroundImage: "none",
                                color: "#fff",
                              }}
                              title={t("filesTab.copyExternalLink", "Copy external link (anyone can open)")}
                            >
                              <i className="fas fa-share-alt" style={{ color: "#fff" }}></i>
                            </button>
                          </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="row">
                <div className="col-md-12">
                    <div className="text-center">
                      <h5 className="card-title">{t('imagesTab.noImagesFound')}</h5>
                      <p className="text-muted">{t('imagesTab.pleaseUploadImages')}</p>
                    </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete}centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('imagesTab.deleteImage')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>Are you sure you want to delete this image?</p>
          <p className='text-muted'>{t('imagesTab.thisActionCannotBeUndone')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button color="secondary" type="button" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button
             style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
          }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteMediaMutation.isLoading}
          >
            {deleteMediaMutation.isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
        </div>
      </Modal>

      {/* Custom Lightbox Modal */}
      {lightboxOpen && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 0,
          padding: 0
        }}>
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              color: 'white',
              textShadow: '0 0 10px rgba(0,0,0,0.8)',
              fontSize: '24px',
              background: 'rgba(0,0,0,0.5)',
              border: 'none',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 100000,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.8)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0,0,0,0.5)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>

          {/* Main Content */}
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            {lightboxImages.length > 0 && (
              <>
                {/* Main Image */}
                <SecureMediaImg
                  key={lightboxImages[currentImageIndex]?.url || currentImageIndex}
                  src={lightboxImages[currentImageIndex]?.url}
                  alt={`Image ${currentImageIndex + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100vh",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  }}
                  fallback={
                    <div style={{ color: "#fff", padding: 24 }}>
                      {t("imagesTab.previewFailed", "Could not load this image.")}
                    </div>
                  }
                />
                
                {/* Navigation Buttons */}
                {lightboxImages.length > 1 && (
                  <>
                    {/* Previous Button */}
                    <button
                      onClick={goToPreviousImage}
                      style={{
                        position: 'absolute',
                        left: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0,0,0,0.7)',
                        border: 'none',
                        width: '50px',
                        height: '50px',
                        color: 'white',
                        fontSize: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        zIndex: 100000
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                    >
                      <i className="tim-icons icon-minimal-left" />
                    </button>
                    
                    {/* Next Button */}
                    <button
                      onClick={goToNextImage}
                      style={{
                        position: 'absolute',
                        right: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0,0,0,0.7)',
                        border: 'none',
                        width: '50px',
                        height: '50px',
                        color: 'white',
                        fontSize: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        zIndex: 100000
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
                    >
                      <i className="tim-icons icon-minimal-right" />
                    </button>
                    
                    {/* Image Counter */}
                    <div style={{
                      position: 'absolute',
                      bottom: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '5px 15px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      zIndex: 100000
                    }}>
                      {currentImageIndex + 1} / {lightboxImages.length}
                    </div>
                  </>
                )}
                
                {/* Thumbnail Navigation */}
                {lightboxImages.length > 1 && (
                  <div style={{
                    position: 'absolute',
                    bottom: '80px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '10px',
                    background: 'rgba(0,0,0,0.8)',
                    padding: '10px',
                    zIndex: 10000
                  }}>
                    {lightboxImages.map((image, index) => (
                      <div
                        key={index}
                        onClick={() => goToImage(index)}
                        style={{
                          width: '60px',
                          height: '40px',
                          cursor: 'pointer',
                          border: index === currentImageIndex ? '2px solid #1d8cf8' : '2px solid transparent',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <SecureMediaImg
                          src={image.thumbnail || image.url}
                          alt={`Thumbnail ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ImagesTab; 